import { Head } from "@inertiajs/react"
import { BellRing, Truck } from "lucide-react"
import { useRef, useState } from "react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { AlertRuleSummary, BreadcrumbItem } from "@/types"

interface Person {
  id: number
  name: string
  email: string
  role: string | null
  notify_alerts: boolean
  notify_deliveries: boolean
}

interface Props {
  facility: { id: number; name: string }
  people: Person[]
  alert_rules: AlertRuleSummary[]
  can_operate: boolean
  can_manage: boolean
  current_user_id: number | null
}

type Kind = "notify_alerts" | "notify_deliveries"

// This facility's alerts and who hears about them. Rules first (what trips),
// recipients second (who gets emailed) — the facility dashboard keeps only the
// header entry point and the per-zone alert badges, so it stays operational
// and this page owns the configuration.
export default function FacilityNotifications({
  facility,
  people: initialPeople,
  alert_rules,
  can_operate: _canOperate,
  can_manage,
  current_user_id,
}: Props) {
  const routes = useOrgRoutes()
  const [people, setPeople] = useState(initialPeople)
  const [sendingDelivery, setSendingDelivery] = useState(false)

  const firingCount = alert_rules.filter(
    (r) => r.enabled && r.status === "firing",
  ).length

  // Moved here from the facility dashboard's notifications card: a sample
  // delivery email to your own address, for confirming recipients live on a
  // setup call. Alert emails have the equivalent per-rule Test button above.
  const sendTestDelivery = async () => {
    setSendingDelivery(true)
    try {
      const res = await fetch(
        `${routes.apiFacilityPath(facility.id)}/delivery_events/test`,
        { method: "POST" },
      )
      if (res.ok) {
        const { recipient } = await res.json()
        toast.success(`Test delivery notification sent to ${recipient}`)
      } else {
        const { error } = await res.json().catch(() => ({ error: "" }))
        toast.error(error || "Could not send test delivery")
      }
    } catch {
      toast.error("Could not send test delivery")
    } finally {
      setSendingDelivery(false)
    }
  }

  // Per-(person, kind) request sequence — same out-of-order PATCH guard as
  // the facility dashboard's notifications card.
  const seq = useRef<Record<string, number>>({})

  const setFlag = (userId: number, kind: Kind, value: boolean) => {
    setPeople((prev) =>
      prev.map((p) => (p.id === userId ? { ...p, [kind]: value } : p)),
    )
  }

  const toggle = async (person: Person, kind: Kind, value: boolean) => {
    const key = `${person.id}:${kind}`
    const mySeq = (seq.current[key] = (seq.current[key] ?? 0) + 1)
    setFlag(person.id, kind, value)

    const self = person.id === current_user_id
    const apiBase = routes.apiFacilityPath(facility.id)
    const url = self
      ? `${apiBase}/notification_preference`
      : `${apiBase}/notification_settings`
    const body = self
      ? { [kind]: value }
      : { user_id: person.id, [kind]: value }

    try {
      const res = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      if (res.ok) return
      throw new Error("request failed")
    } catch {
      if (mySeq !== seq.current[key]) return
      toast.error(`Could not update ${person.name}'s notifications`)
      setFlag(person.id, kind, !value)
    }
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Facilities", href: routes.facilitiesPath() },
    { title: facility.name, href: routes.facilityPath(facility.id) },
    { title: "Alerts", href: routes.facilityNotificationsPath(facility.id) },
  ]

  const enabledCount = alert_rules.filter((r) => r.enabled).length
  const summary =
    enabledCount === 0
      ? "No alerts configured yet"
      : `${enabledCount} active ${enabledCount === 1 ? "alert" : "alerts"}` +
        (firingCount > 0 ? ` · ${firingCount} firing now` : " · all clear")

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Alerts — ${facility.name}`} />
      <div className="mx-auto w-full max-w-4xl space-y-6 p-6">
        <div className="flex items-center gap-3">
          <BellRing className="text-muted-foreground size-6" />
          <div>
            <h1 className="text-xl font-semibold">
              Alerts &amp; notifications
            </h1>
            <p className="text-muted-foreground text-sm">
              {facility.name} — {summary}
            </p>
          </div>
        </div>

        {/* TODO Phase 2: alert-rule editor, rebuilt against the new
            AlertRule shape (bo_not_in_shop_days, dispo_waiting_days,
            pod_missed, sla_breach, rider_expiring_days, requal_due_days). */}

        <Card>
          <CardHeader>
            <CardTitle>{can_manage ? "Recipients" : "Your emails"}</CardTitle>
            <CardDescription>
              {can_manage
                ? "Everyone with access to this facility. "
                : "Your notification settings for this facility — facility admins manage the full recipient list. "}
              Alerts are threshold emails (low fill, days of material, volume);
              deliveries are auto-detected inbound loads.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {people.length === 0 ? (
              <p className="text-muted-foreground py-4 text-center text-sm">
                Nobody has access to this facility yet.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Person</TableHead>
                    <TableHead>Access</TableHead>
                    <TableHead className="text-right">Alert emails</TableHead>
                    <TableHead className="text-right">
                      Delivery emails
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {people.map((person) => {
                    const editable = can_manage || person.id === current_user_id
                    return (
                      <TableRow key={person.id}>
                        <TableCell>
                          <div className="font-medium">
                            {person.name}
                            {person.id === current_user_id && (
                              <span className="text-muted-foreground ml-1.5 text-xs">
                                (you)
                              </span>
                            )}
                          </div>
                          <div className="text-muted-foreground text-xs">
                            {person.email}
                          </div>
                        </TableCell>
                        <TableCell>
                          {person.role ? (
                            <Badge variant="outline" className="capitalize">
                              {person.role}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Switch
                            checked={person.notify_alerts}
                            disabled={!editable}
                            onCheckedChange={(v) =>
                              toggle(person, "notify_alerts", v)
                            }
                            aria-label={`Alert emails for ${person.name}`}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Switch
                            checked={person.notify_deliveries}
                            disabled={!editable}
                            onCheckedChange={(v) =>
                              toggle(person, "notify_deliveries", v)
                            }
                            aria-label={`Delivery emails for ${person.name}`}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t pt-4">
              <p className="text-muted-foreground text-xs">
                In-app notifications (the bell) always go to everyone with
                access — these switches control email only. Every alert email
                carries a one-click unsubscribe link.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={sendTestDelivery}
                disabled={sendingDelivery}
              >
                <Truck className="mr-1 size-4" />
                {sendingDelivery ? "Sending…" : "Send test delivery"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
